import { Fragment, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, addDoc, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useT } from '../../contexts/LocaleContext';
import { LLS_GROUPS, isLlsGroupType } from '../../constants/llsCentres';
import { createPortalUser } from '../../utils/createUser';

const FALLBACK_SPORTS = [
  'Football', 'Rugby Union', 'Rugby League', 'Cricket', 'Hockey',
  'Basketball', 'Netball', 'Tennis', 'Golf', 'Swimming',
  'Athletics', 'Multi-Sport', 'Other',
];

const FALLBACK_GROUP_TYPES = [
  'School', 'College / 6th Form', 'University', 'Sports Club',
  'Corporate', 'Private Group', 'MIC Cup', 'Other',
];

// MIC Cup tours run 3-10 nights (vs. the standard 2/3/4 quick-pick).
// Matched case/whitespace-insensitively since the live "Mic Cup" group type
// option (Firestore groupTypes collection, plus older saved tours) predates
// the "MIC Cup" display-name change.
const MIC_CUP_NIGHT_OPTIONS = [3, 4, 5, 6, 7, 8, 9, 10];
const isMicCupGroupType = (v) => (v || '').trim().toLowerCase() === 'mic cup';

const FALLBACK_CATEGORIES = [
  'Hotel & Food', 'Flights', 'Transport', 'Match Tickets', 'Stadium Tour',
  'Fixtures', 'Training Sessions', 'Padel', 'Yoga', 'Waterpark',
];

const QUANTITY_ADDONS = {
  'Fixtures':          { max: 5, label: 'Fixtures per squad' },
  'Training Sessions': { max: 5, label: 'Training sessions per squad' },
  'Padel':             { max: 3 },
  'Yoga':              { max: 3 },
};
const OPTION_ADDONS = {};

const STANDARD_PKG_NAMES = new Set(['Hotel & Food', 'Flights', 'Fixtures', 'Match Tickets', 'Training Sessions', 'Stadium Tour', 'Padel', 'Transport']);

// Padelante is a padel-only brand: one sport, and a fixed inclusions list.
const PADELANTE_CATEGORY_NAMES = ['Hotel & Food', 'Flights', 'Transport', 'Padel'];
const PADELANTE_STANDARD_PKG = new Set(PADELANTE_CATEGORY_NAMES);
// Padelante uses its own short group-type list (padel is always included, so no
// session count is offered — see the QUANTITY_ADDONS guard below).
const PADELANTE_GROUP_TYPES = ['Padel club', 'Corporate event', 'Private group'];
const PADELANTE_SPORT_TILE = { name: 'Padel', emoji: '🎾' };

const SPORT_TILES = [
  { name: 'Football',   emoji: '⚽' },
  { name: 'Rugby',      emoji: '🏉' },
  { name: 'Basketball', emoji: '🏀' },
  { name: 'Tennis',     emoji: '🎾' },
  { name: 'Swimming',   emoji: '🏊' },
  { name: 'Hockey',     emoji: '🏑' },
  { name: 'Cricket',    emoji: '🏏' },
];

const BRANDS = [
  { id: 'mst',       label: 'MST Sports Tours' },
  { id: 'your_tour', label: 'Your Tour' },
  { id: 'padelante', label: 'Padelante' },
];

const BRAND_BADGES = {
  your_tour: { label: 'YT',  className: 'bg-navy text-white' },
  padelante: { label: 'PAD', className: 'bg-[#8DC63F] text-[#1B4079]' },
};

const BLANK_TOUR = {
  groupName: '',
  sport: '', destination: '', departureDate: '', returnDate: '',
  groupType: '', llsGroup: '', participants: '', participantsMale: '', participantsFemale: '',
  supportersMale: '', supportersFemale: '',
  customerStaffMale: '', customerStaffFemale: '',
  numberOfSquads: '', contactName: '', phone: '', notes: '',
};

function CheckIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function FieldError({ msg }) {
  if (!msg) return null;
  return <p className="mt-1 text-[12px] text-dr">{msg}</p>;
}

function formatDate(d) {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

function ReviewRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex gap-3">
      <span className="text-[13px] text-[#6B7280] w-40 flex-shrink-0">{label}</span>
      <span className="text-[13px] text-[#111827] font-medium">{value}</span>
    </div>
  );
}

export default function StaffNewRequest({ market = 'uk_to_spain', basePath = '/staff', lockedBrand = null }) {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  const stepLabels = [t('newTour.stepCustomer'), t('newTour.stepTourDetails'), t('newTour.stepGroupInfo'), t('newTour.stepReview')];

  // A locked brand (e.g. the Padelante /pad portal) forces every tour to that
  // brand — the picker collapses to the single option. Otherwise Spain → UK
  // tours are never booked under the Padelante brand.
  const availableBrands = lockedBrand
    ? BRANDS.filter((b) => b.id === lockedBrand)
    : market === 'spain_to_uk'
      ? BRANDS.filter((b) => b.id !== 'padelante')
      : BRANDS;

  const [step, setStep] = useState(1);
  const [brand, setBrand] = useState(lockedBrand || 'mst');
  const [fieldErrors, setFieldErrors] = useState({});

  // ─── Customer state ──────────────────────────────────────────────────────────
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [mode, setMode] = useState('existing');
  const [newCustomer, setNewCustomer] = useState({ name: '', email: '', clubSchool: '' });
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const dropdownRef = useRef(null);

  // ─── Tour state ───────────────────────────────────────────────────────────────
  const [form, setForm] = useState(BLANK_TOUR);
  const [showMoreSports, setShowMoreSports] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ─── Sports + categories + group types ───────────────────────────────────────
  const [sportsList, setSportsList] = useState(FALLBACK_SPORTS);
  const [categories, setCategories] = useState([]);
  const [groupTypesList, setGroupTypesList] = useState(FALLBACK_GROUP_TYPES);
  const [selectedAddons, setSelectedAddons] = useState({});
  const [packageMode, setPackageMode] = useState('standard');

  useEffect(() => {
    getDocs(query(collection(db, 'users'), where('role', '==', 'customer')))
      .then((snap) => setCustomers(snap.docs.map((d) => ({ uid: d.id, ...d.data() }))));

    getDocs(query(collection(db, 'sports'), orderBy('order', 'asc'))).then((snap) => {
      if (!snap.empty) {
        setSportsList(
          snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((s) => s.active !== false).map((s) => s.name)
        );
      }
    }).catch(() => {});

    getDocs(query(collection(db, 'tourCategories'), orderBy('order', 'asc'))).then((snap) => {
      if (!snap.empty) setCategories(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      else setCategories(FALLBACK_CATEGORIES.map((name, i) => ({ id: name, name, order: i })));
    }).catch(() => {
      setCategories(FALLBACK_CATEGORIES.map((name, i) => ({ id: name, name, order: i })));
    });

    getDocs(query(collection(db, 'groupTypes'), orderBy('order', 'asc'))).then((snap) => {
      if (!snap.empty) {
        setGroupTypesList(
          snap.docs.map((d) => d.data()).filter((g) => g.active !== false).map((g) => g.name)
        );
      }
    }).catch(() => {});
  }, []);

  // Apply standard package when mode is standard and categories/dates change
  useEffect(() => {
    if (categories.length === 0 || packageMode !== 'standard') return;
    const nights = (() => {
      if (!form.departureDate || !form.returnDate) return 2;
      const diff = Math.round((new Date(form.returnDate) - new Date(form.departureDate)) / 86400000);
      return diff > 0 ? diff : 2;
    })();
    const fixtureQty = nights >= 3 ? 2 : 1;
    const pkgSet = brand === 'padelante' ? PADELANTE_STANDARD_PKG : STANDARD_PKG_NAMES;
    const defaults = {};
    categories.forEach((cat) => {
      if (!pkgSet.has(cat.name)) return;
      const base = { name: cat.name, notes: '' };
      if (cat.name === 'Fixtures') defaults[cat.id] = { ...base, quantity: fixtureQty };
      else if (cat.name === 'Training Sessions') defaults[cat.id] = { ...base, quantity: 2 };
      else if (QUANTITY_ADDONS[cat.name] && !(brand === 'padelante' && cat.name === 'Padel')) defaults[cat.id] = { ...base, quantity: 1 };
      else if (cat.name === 'Flights') defaults[cat.id] = { ...base, largeCaseQuantity: 0 };
      else defaults[cat.id] = base;
    });
    setSelectedAddons(defaults);
  }, [packageMode, form.departureDate, form.returnDate, categories, brand]); // eslint-disable-line react-hooks/exhaustive-deps

  // Padelante is padel-only, Alicante-only — pre-fill sport + destination.
  useEffect(() => {
    if (brand === 'padelante') {
      setForm((prev) => (prev.sport === 'Padel' && prev.destination === 'Alicante')
        ? prev
        : { ...prev, sport: 'Padel', destination: 'Alicante' });
    }
  }, [brand]);

  // Close search dropdown on outside click
  useEffect(() => {
    function handler(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = customers.filter((c) => {
    const q = search.toLowerCase();
    return c.email?.toLowerCase().includes(q) || c.name?.toLowerCase().includes(q);
  }).slice(0, 8);

  function selectCustomer(c) {
    setSelectedCustomer(c);
    setSearch(c.email);
    setShowDropdown(false);
    clearError('customer');
  }

  function clearCustomer() {
    setSelectedCustomer(null);
    setSearch('');
  }

  async function handleCreateCustomer() {
    const errs = {};
    if (!newCustomer.name.trim()) errs.newName = 'Name is required.';
    if (!newCustomer.email.trim()) errs.newEmail = 'Email is required.';
    if (Object.keys(errs).length > 0) { setFieldErrors((p) => ({ ...p, ...errs })); return; }

    setCreatingCustomer(true);
    setFieldErrors((p) => { const n = { ...p }; delete n.newName; delete n.newEmail; delete n.createCustomer; return n; });
    try {
      const uid = await createPortalUser({
        email: newCustomer.email,
        role: 'customer',
        name: newCustomer.name,
        clubSchool: newCustomer.clubSchool,
        createdByEmail: currentUser.email,
        brand,
      });
      const c = { uid, email: newCustomer.email.trim().toLowerCase(), name: newCustomer.name.trim() };
      setCustomers((prev) => [...prev, c]);
      setSelectedCustomer(c);
      setMode('existing');
      setSearch(c.email);
      setNewCustomer({ name: '', email: '', clubSchool: '' });
    } catch (err) {
      setFieldErrors((p) => ({
        ...p,
        createCustomer: err.code === 'auth/email-already-in-use'
          ? 'An account with this email already exists.'
          : 'Failed to create customer. Please try again.',
      }));
    } finally {
      setCreatingCustomer(false);
    }
  }

  function clearError(key) {
    setFieldErrors((p) => { const n = { ...p }; delete n[key]; return n; });
  }

  function applyNightPackage(nights) {
    if (!form.departureDate) return;
    const dep = new Date(form.departureDate);
    dep.setDate(dep.getDate() + nights);
    setForm((prev) => ({ ...prev, returnDate: dep.toISOString().slice(0, 10) }));
    clearError('returnDate');
  }

  function handleChange(e) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    clearError(e.target.name);
  }

  function setSport(s) {
    setForm((prev) => ({ ...prev, sport: s }));
    setShowMoreSports(false);
    clearError('sport');
  }

  function adjustCount(name, delta) {
    setForm((prev) => ({
      ...prev,
      [name]: String(Math.max(0, (parseInt(prev[name]) || 0) + delta)),
    }));
  }

  function toggleAddon(cat) {
    setSelectedAddons((prev) => {
      if (prev[cat.id]) {
        const next = { ...prev };
        delete next[cat.id];
        return next;
      }
      const base = { name: cat.name, notes: '' };
      if (QUANTITY_ADDONS[cat.name]) return { ...prev, [cat.id]: { ...base, quantity: 1 } };
      if (cat.name === 'Flights') return { ...prev, [cat.id]: { ...base, largeCaseQuantity: 0 } };
      return { ...prev, [cat.id]: base };
    });
  }

  function updateAddonQuantity(catId, qty) {
    setSelectedAddons((prev) => ({ ...prev, [catId]: { ...prev[catId], quantity: qty } }));
  }

  function updateAddonNotes(catId, notes) {
    setSelectedAddons((prev) => ({ ...prev, [catId]: { ...prev[catId], notes } }));
  }

  function updateAddonLargeCaseQty(catId, qty) {
    setSelectedAddons((prev) => ({ ...prev, [catId]: { ...prev[catId], largeCaseQuantity: Math.max(0, qty) } }));
  }

  function validateStep(n) {
    const errs = {};
    if (n === 1) {
      if (!selectedCustomer) errs.customer = 'Please select or create a customer.';
      if (!form.contactName.trim()) errs.contactName = 'Contact name is required.';
    }
    if (n === 2) {
      if (!form.sport) errs.sport = 'Please select a sport.';
      if (!form.destination.trim()) errs.destination = 'Destination is required.';
      if (!form.departureDate) errs.departureDate = 'Departure date is required.';
      if (!form.returnDate) errs.returnDate = 'Return date is required.';
      if (form.returnDate && form.departureDate && form.returnDate < form.departureDate) {
        errs.returnDate = 'Return date must be after departure date.';
      }
    }
    if (n === 3) {
      if (!form.groupType) errs.groupType = 'Group type is required.';
      if (!form.participantsMale && !form.participantsFemale) errs.participantsMale = 'Please enter the number of male and/or female players.';
    }
    return errs;
  }

  function goNext() {
    const errs = validateStep(step);
    if (Object.keys(errs).length > 0) { setFieldErrors(errs); return; }
    setFieldErrors({});
    setStep((s) => s + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function goBack() {
    setFieldErrors({});
    setStep((s) => s - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function handleSubmit() {
    const addonsArray = Object.entries(selectedAddons).map(([id, val]) => ({
      categoryId: id,
      categoryName: val.name,
      notes: val.notes || '',
      ...(val.quantity !== undefined ? { quantity: val.quantity } : {}),
    }));

    const flightsAddon = Object.values(selectedAddons).find((v) => v.name === 'Flights');
    const largeCaseQuantity = flightsAddon ? Number(flightsAddon.largeCaseQuantity) || 0 : 0;

    setSubmitting(true);
    setFieldErrors({});
    try {
      const ref = await addDoc(collection(db, 'requests'), {
        customerId: selectedCustomer.uid,
        customerEmail: selectedCustomer.email,
        leaderCostBasis: 'days', // Tour/Junior Lead costed by calendar days present, not nights stayed
        internalRef: form.groupName.trim(),
        sport: form.sport,
        destination: form.destination,
        departureDate: form.departureDate,
        returnDate: form.returnDate,
        groupType: form.groupType,
        llsGroup: isLlsGroupType(form.groupType) ? form.llsGroup || null : null,
        participants: (Number(form.participantsMale) || 0) + (Number(form.participantsFemale) || 0),
        numberOfSquads: form.numberOfSquads ? Number(form.numberOfSquads) : null,
        contactName: form.contactName,
        phone: form.phone,
        notes: form.notes,
        selectedAddons: addonsArray,
        largeCaseQuantity,
        participantsMale: form.participantsMale ? Number(form.participantsMale) : null,
        participantsFemale: form.participantsFemale ? Number(form.participantsFemale) : null,
        supportersMale: form.supportersMale ? Number(form.supportersMale) : 0,
        supportersFemale: form.supportersFemale ? Number(form.supportersFemale) : 0,
        customerStaffMale: form.customerStaffMale ? Number(form.customerStaffMale) : 0,
        customerStaffFemale: form.customerStaffFemale ? Number(form.customerStaffFemale) : 0,
        customerStaff: (Number(form.customerStaffMale) || 0) + (Number(form.customerStaffFemale) || 0),
        status: 'submitted',
        category: 'sales_admin',
        archived: false,
        stageProgress: {},
        brand: brand,
        market: market,
        createdAt: serverTimestamp(),
        createdByStaff: currentUser.email,
      });
      // Padelante tours live in the /pad portal — send staff there even when the
      // tour was created from the /staff New-Tour brand picker.
      const destBase = brand === 'padelante' ? '/pad' : basePath;
      navigate(`${destBase}/requests/${ref.id}`);
    } catch (err) {
      console.error(err);
      setFieldErrors({ submit: 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  }

  const isPadelante = brand === 'padelante';
  const sportTiles = isPadelante ? [PADELANTE_SPORT_TILE] : SPORT_TILES;
  const isTileSport = SPORT_TILES.some((t) => t.name === form.sport);

  return (
    <div className="space-y-6">

      {/* ─── Page header ────────────────────────────────────────────────────── */}
      <div>
        <button
          onClick={() => navigate(`${basePath}/requests`)}
          className="text-[13px] text-[#9CA3AF] hover:text-[#374151] mb-2 block"
        >
          {t('newTour.backToRequests')}
        </button>
        <h2 className="text-[20px] font-bold text-[#111827]">{t('newTour.title')}</h2>
        <p className="text-[14px] text-[#6B7280] mt-1">{t('newTour.subtitle')}</p>
      </div>

      {/* ─── Step indicator ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-[12px] border border-[#E5E7EB] px-6 py-5">
        <div className="flex items-start">
          {stepLabels.map((label, i) => {
            const n = i + 1;
            const done = n < step;
            const current = n === step;
            return (
              <Fragment key={n}>
                {i > 0 && (
                  <div className={`flex-1 h-px mt-4 mx-2 ${done ? 'bg-pb' : 'bg-[#E5E7EB]'}`} />
                )}
                <div className="flex flex-col items-center gap-1.5">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-bold border-2 transition-all ${
                    done    ? 'bg-pb border-pb text-white' :
                    current ? 'border-pb text-pb bg-blue-light' :
                              'border-[#E5E7EB] text-[#9CA3AF] bg-white'
                  }`}>
                    {done ? <CheckIcon /> : n}
                  </div>
                  <span className={`text-[11px] font-medium whitespace-nowrap ${
                    current ? 'text-pb' : done ? 'text-[#374151]' : 'text-[#9CA3AF]'
                  }`}>{label}</span>
                </div>
              </Fragment>
            );
          })}
        </div>
      </div>

      {/* ─── Step 1: Brand selector ──────────────────────────────────────────── */}
      {step === 1 && (
        <section className="bg-white rounded-[12px] border border-[#E5E7EB] p-5">
          <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-3">{t("newTour.brand")}</h3>
          <div className="flex gap-3">
            {availableBrands.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => setBrand(b.id)}
                className={`flex items-center gap-2 px-5 py-3 rounded-[10px] border-2 text-[13px] font-medium transition ${
                  brand === b.id
                    ? 'border-pb bg-blue-light text-pb'
                    : 'border-[#E5E7EB] bg-white text-[#6B7280] hover:border-[#D1D5DB]'
                }`}
              >
                {BRAND_BADGES[b.id] && (
                  <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-[4px] ${BRAND_BADGES[b.id].className}`}>{BRAND_BADGES[b.id].label}</span>
                )}
                {b.label}
              </button>
            ))}
          </div>
          {brand === 'your_tour' && (
            <p className="mt-3 text-[12px] text-pb bg-blue-light border border-pb/20 rounded-[8px] px-3 py-2">
              This request will be managed under the <strong>Your Tour</strong> brand. Invoices will be raised for the Your Tour finance team to send directly.
            </p>
          )}
          {brand === 'padelante' && (
            <p className="mt-3 text-[12px] text-pb bg-blue-light border border-pb/20 rounded-[8px] px-3 py-2">
              This request will be managed under the <strong>Padelante</strong> brand. Customer-facing PDFs, emails and invoices will use Padelante branding.
            </p>
          )}
        </section>
      )}

      {/* ─── Step 1: Customer ────────────────────────────────────────────────── */}
      {step === 1 && (
        <section className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">{t("newTour.customer")}</h3>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setMode('existing'); setFieldErrors({}); }}
                className={`text-[11px] px-3 py-1 rounded-[6px] font-medium transition ${mode === 'existing' ? 'bg-pb text-white' : 'border border-[#E5E7EB] text-[#6B7280] hover:bg-surface'}`}
              >
                Existing customer
              </button>
              <button
                type="button"
                onClick={() => { setMode('new'); setSelectedCustomer(null); setSearch(''); setFieldErrors({}); }}
                className={`text-[11px] px-3 py-1 rounded-[6px] font-medium transition ${mode === 'new' ? 'bg-pb text-white' : 'border border-[#E5E7EB] text-[#6B7280] hover:bg-surface'}`}
              >
                New customer
              </button>
            </div>
          </div>

          {mode === 'existing' ? (
            <div className="relative" ref={dropdownRef}>
              <label className="block text-[13px] font-medium text-[#374151] mb-1">Search by name or email</label>
              <div className="relative">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setSelectedCustomer(null); setShowDropdown(true); }}
                  onFocus={() => setShowDropdown(true)}
                  placeholder="Type to search customers…"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb pr-8"
                />
                {selectedCustomer && (
                  <button type="button" onClick={clearCustomer} className="absolute right-2.5 top-2.5 text-[#9CA3AF] hover:text-[#6B7280] text-[12px]">✕</button>
                )}
              </div>
              {showDropdown && search && !selectedCustomer && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-[#E5E7EB] rounded-[8px] shadow-lg overflow-hidden">
                  {filtered.length === 0
                    ? <p className="px-4 py-3 text-[13px] text-[#9CA3AF]">No customers found.</p>
                    : filtered.map((c) => (
                      <button key={c.uid} type="button" onClick={() => selectCustomer(c)}
                        className="w-full text-left px-4 py-2.5 hover:bg-surface transition">
                        <p className="text-[13px] font-medium text-[#111827]">{c.name || c.email}</p>
                        {c.name && <p className="text-[12px] text-[#9CA3AF]">{c.email}</p>}
                      </button>
                    ))
                  }
                </div>
              )}
              {selectedCustomer && (
                <div className="mt-2 flex items-center gap-2 text-[13px] text-[#065F46] bg-green-light border border-sg/20 rounded-[8px] px-3 py-2">
                  <span className="w-2 h-2 rounded-full bg-sg flex-shrink-0" />
                  <span className="font-medium">{selectedCustomer.name || selectedCustomer.email}</span>
                  {selectedCustomer.name && <span className="text-sg">· {selectedCustomer.email}</span>}
                </div>
              )}
              <FieldError msg={fieldErrors.customer} />
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Club / school</label>
                <input
                  type="text" value={newCustomer.clubSchool}
                  onChange={(e) => setNewCustomer({ ...newCustomer, clubSchool: e.target.value })}
                  placeholder="e.g. Dixons Academy"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
                <p className="text-[11px] text-[#9CA3AF] mt-1">Used as the organisation name in the returning-clients database.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] font-medium text-[#374151] mb-1">{t("newTour.contactName")} <span className="text-dr">*</span></label>
                  <input
                    type="text" value={newCustomer.name}
                    onChange={(e) => { setNewCustomer({ ...newCustomer, name: e.target.value }); clearError('newName'); }}
                    placeholder="e.g. John Smith"
                    className={`w-full border rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb ${fieldErrors.newName ? 'border-dr' : 'border-[#E5E7EB]'}`}
                  />
                  <FieldError msg={fieldErrors.newName} />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#374151] mb-1">Email Address <span className="text-dr">*</span></label>
                  <input
                    type="email" value={newCustomer.email}
                    onChange={(e) => { setNewCustomer({ ...newCustomer, email: e.target.value }); clearError('newEmail'); }}
                    placeholder="e.g. john@school.ac.uk"
                    className={`w-full border rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb ${fieldErrors.newEmail ? 'border-dr' : 'border-[#E5E7EB]'}`}
                  />
                  <FieldError msg={fieldErrors.newEmail} />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button" onClick={handleCreateCustomer} disabled={creatingCustomer}
                  className="px-[18px] py-[9px] bg-pb hover:bg-navy text-white text-[13px] font-semibold rounded-[8px] disabled:opacity-60 transition"
                >
                  {creatingCustomer ? t("newTour.creatingCustomer") : t("newTour.createSelectCustomer")}
                </button>
                <p className="text-[12px] text-[#9CA3AF]">They'll receive an email to set their password.</p>
              </div>
              {fieldErrors.createCustomer && (
                <p className="text-[12px] text-dr">{fieldErrors.createCustomer}</p>
              )}
              {selectedCustomer && (
                <div className="flex items-center gap-2 text-[13px] text-[#065F46] bg-green-light border border-sg/20 rounded-[8px] px-3 py-2">
                  <span className="w-2 h-2 rounded-full bg-sg flex-shrink-0" />
                  <span className="font-medium">{selectedCustomer.name}</span>
                  <span className="text-sg">· {selectedCustomer.email} — account created</span>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* ─── Step 1: Group details ───────────────────────────────────────────── */}
      {step === 1 && (
        <section className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 space-y-4">
          <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">Group details</h3>
          <div>
            <label className="block text-[13px] font-medium text-[#374151] mb-1">Group name</label>
            <input
              type="text" name="groupName" value={form.groupName} onChange={handleChange}
              placeholder="e.g. St Kevin's FC U16s"
              className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
            />
            <p className="text-[11px] text-[#9CA3AF] mt-1">Shown as the tour name in your tours list. Leave blank to use sport — destination.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1">{t("newTour.contactName")} <span className="text-dr">*</span></label>
              <input
                type="text" name="contactName" value={form.contactName} onChange={handleChange}
                placeholder="Full name"
                className={`w-full border rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb ${fieldErrors.contactName ? 'border-dr' : 'border-[#E5E7EB]'}`}
              />
              <FieldError msg={fieldErrors.contactName} />
            </div>
            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1">{t("newTour.phone")}</label>
              <input
                type="tel" name="phone" value={form.phone} onChange={handleChange}
                placeholder="Optional"
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
              />
            </div>
          </div>
        </section>
      )}

      {/* ─── Step 2: Tour Details ─────────────────────────────────────────────── */}
      {step === 2 && (
        <div className="space-y-5">
          {/* Sport + destination + dates */}
          <section className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 space-y-5">
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">{t("newTour.tourDetails")}</h3>

            {/* Sport tiles */}
            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-2">
                Sport <span className="text-dr">*</span>
              </label>
              <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                {sportTiles.map((tile) => (
                  <button
                    key={tile.name}
                    type="button"
                    onClick={() => setSport(tile.name)}
                    className={`flex flex-col items-center gap-1 py-3 px-2 rounded-[10px] border-2 text-[11px] font-medium transition ${
                      form.sport === tile.name
                        ? 'border-pb bg-blue-light text-pb'
                        : 'border-[#E5E7EB] bg-white text-[#6B7280] hover:border-[#D1D5DB] hover:bg-surface'
                    }`}
                  >
                    <span className="text-2xl leading-none">{tile.emoji}</span>
                    <span>{tile.name}</span>
                  </button>
                ))}
                {/* More tile — hidden for Padelante (padel-only) */}
                {!isPadelante && (
                  <button
                    type="button"
                    onClick={() => setShowMoreSports((v) => !v)}
                    className={`flex flex-col items-center gap-1 py-3 px-2 rounded-[10px] border-2 text-[11px] font-medium transition ${
                      !isTileSport && form.sport
                        ? 'border-pb bg-blue-light text-pb'
                        : 'border-[#E5E7EB] bg-white text-[#6B7280] hover:border-[#D1D5DB] hover:bg-surface'
                    }`}
                  >
                    <span className="text-2xl leading-none">⋯</span>
                    <span>{!isTileSport && form.sport ? form.sport : 'More'}</span>
                  </button>
                )}
              </div>
              {!isPadelante && showMoreSports && (
                <div className="mt-3">
                  <select
                    value={form.sport}
                    onChange={(e) => setSport(e.target.value)}
                    className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                    autoFocus
                  >
                    <option value="">Select a sport…</option>
                    {sportsList.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              )}
              <FieldError msg={fieldErrors.sport} />
            </div>

            {/* Destination + dates */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Destination <span className="text-dr">*</span></label>
                <input
                  type="text" name="destination" value={form.destination} onChange={handleChange}
                  placeholder="e.g. Barcelona, Spain"
                  readOnly={isPadelante}
                  className={`w-full border rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb ${fieldErrors.destination ? 'border-dr' : 'border-[#E5E7EB]'} ${isPadelante ? 'bg-[#F9FAFB] text-[#6B7280]' : ''}`}
                />
                <FieldError msg={fieldErrors.destination} />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">{t("newTour.departureDate")} <span className="text-dr">*</span></label>
                <input
                  type="date" name="departureDate" value={form.departureDate} onChange={handleChange}
                  className={`w-full border rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb ${fieldErrors.departureDate ? 'border-dr' : 'border-[#E5E7EB]'}`}
                />
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <span className="text-[11px] text-[#9CA3AF]">Quick:</span>
                  {isMicCupGroupType(form.groupType) ? (
                    // MIC Cup tours run 3-10 nights — too many options for a button row, so use a select.
                    <select
                      value=""
                      onChange={(e) => { if (e.target.value) applyNightPackage(Number(e.target.value)); }}
                      disabled={!form.departureDate}
                      title={!form.departureDate ? 'Set a departure date first' : 'Set return date by number of nights'}
                      className="text-[11px] pl-2 pr-1 py-1 rounded-[6px] border border-[#E5E7EB] text-[#6B7280] hover:border-pb/40 hover:text-pb disabled:opacity-40 disabled:cursor-not-allowed transition bg-white"
                    >
                      <option value="">Select nights…</option>
                      {MIC_CUP_NIGHT_OPTIONS.map((n) => (
                        <option key={n} value={n}>{n} nights</option>
                      ))}
                    </select>
                  ) : (
                    [2, 3, 4, 7].map((n) => (
                      <button
                        key={n} type="button"
                        onClick={() => applyNightPackage(n)}
                        disabled={!form.departureDate}
                        title={!form.departureDate ? 'Set a departure date first' : `Set return to ${n} nights after departure`}
                        className="text-[11px] px-2.5 py-1 rounded-[6px] border border-[#E5E7EB] text-[#6B7280] hover:border-pb/40 hover:text-pb disabled:opacity-40 disabled:cursor-not-allowed transition"
                      >
                        {n} nights
                      </button>
                    ))
                  )}
                </div>
                <FieldError msg={fieldErrors.departureDate} />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">{t("newTour.returnDate")} <span className="text-dr">*</span></label>
                <input
                  type="date" name="returnDate" value={form.returnDate} onChange={handleChange}
                  className={`w-full border rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb ${fieldErrors.returnDate ? 'border-dr' : 'border-[#E5E7EB]'}`}
                />
                <FieldError msg={fieldErrors.returnDate} />
              </div>
            </div>
          </section>

          {/* What's included */}
          <section className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">{t("newTour.whatsIncluded")}</h3>
                <div className="flex rounded-[8px] border border-[#E5E7EB] overflow-hidden text-[12px]">
                  <button type="button" onClick={() => setPackageMode('standard')}
                    className={`px-3 py-1.5 font-medium transition ${packageMode === 'standard' ? 'bg-pb text-white' : 'bg-white text-[#6B7280] hover:bg-[#F3F4F6]'}`}>
                    Standard package
                  </button>
                  <button type="button" onClick={() => setPackageMode('bespoke')}
                    className={`px-3 py-1.5 font-medium border-l border-[#E5E7EB] transition ${packageMode === 'bespoke' ? 'bg-pb text-white' : 'bg-white text-[#6B7280] hover:bg-[#F3F4F6]'}`}>
                    Bespoke
                  </button>
                </div>
              </div>
              <p className="text-[12px] text-[#9CA3AF]">
                {packageMode === 'standard'
                  ? 'Pre-filled based on tour duration — edit freely or switch to Bespoke for full control. Changing dates will update the selection.'
                  : 'Select everything the customer wants included. Hotel &amp; Food and Flights are pre-selected.'}
              </p>
            </div>
            {categories.length === 0 ? (
              <p className="text-[13px] text-[#9CA3AF]">Loading options…</p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {categories.filter((c) => c.active !== false && (!isPadelante || PADELANTE_CATEGORY_NAMES.includes(c.name))).map((cat) => {
                    const selected = !!selectedAddons[cat.id];
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => toggleAddon(cat)}
                        className={`px-3.5 py-1.5 rounded-[20px] border text-[13px] font-medium transition ${
                          selected
                            ? 'bg-pb border-pb text-white'
                            : 'bg-white border-[#E5E7EB] text-[#374151] hover:border-pb/40 hover:bg-blue-light'
                        }`}
                      >
                        {cat.name}
                      </button>
                    );
                  })}
                </div>

                {/* Sub-options for selected addons */}
                {Object.keys(selectedAddons).length > 0 && (
                  <div className="space-y-4 pt-3 border-t border-[#F3F4F6]">
                    <p className="text-[11px] font-medium text-[#9CA3AF] uppercase tracking-[0.9px]">Additional preferences</p>
                    {Object.entries(selectedAddons).map(([id, val]) => {
                      // Padelante includes padel in every tour — no session count.
                      const qtyConfig = (isPadelante && val.name === 'Padel') ? null : QUANTITY_ADDONS[val.name];

                      if (qtyConfig) return (
                        <div key={id}>
                          <label className="block text-[13px] font-medium text-[#374151] mb-2">{qtyConfig.label || val.name}</label>
                          <div className="flex gap-2">
                            {Array.from({ length: qtyConfig.max }, (_, i) => i + 1).map((n) => (
                              <button key={n} type="button" onClick={() => updateAddonQuantity(id, n)}
                                className={`w-10 h-10 rounded-[8px] text-[13px] font-semibold border transition ${val.quantity === n ? 'bg-pb border-pb text-white' : 'bg-white border-[#E5E7EB] text-[#374151] hover:border-pb/40'}`}>
                                {n}
                              </button>
                            ))}
                          </div>
                        </div>
                      );

                      return (
                        <div key={id} className="space-y-2">
                          <label className="block text-[13px] font-medium text-[#374151]">{val.name}</label>
                          {val.name === 'Flights' && (
                            <div className="flex items-center gap-3">
                              <span className="text-[13px] text-[#6B7280]">Large kit cases (group total)</span>
                              <button type="button" onClick={() => updateAddonLargeCaseQty(id, (val.largeCaseQuantity || 0) - 1)}
                                className="w-7 h-7 rounded-full border border-[#E5E7EB] text-[#374151] hover:border-pb/40 flex items-center justify-center text-[16px] leading-none">−</button>
                              <span className="w-6 text-center text-[14px] font-semibold text-[#111827]">{val.largeCaseQuantity || 0}</span>
                              <button type="button" onClick={() => updateAddonLargeCaseQty(id, (val.largeCaseQuantity || 0) + 1)}
                                className="w-7 h-7 rounded-full border border-[#E5E7EB] text-[#374151] hover:border-pb/40 flex items-center justify-center text-[16px] leading-none">+</button>
                              <span className="text-[11px] text-[#9CA3AF]">0 = hand luggage only</span>
                            </div>
                          )}
                          <input type="text" value={val.notes} onChange={(e) => updateAddonNotes(id, e.target.value)}
                            placeholder={`Specific requirements for ${val.name.toLowerCase()}…`}
                            className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb" />
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      )}

      {/* ─── Step 3: Group Info ───────────────────────────────────────────────── */}
      {step === 3 && (
        <section className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 space-y-5">
          <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">{t("newTour.groupInfo")}</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-[13px] font-medium text-[#374151] mb-1">Group Type <span className="text-dr">*</span></label>
              <select
                name="groupType" value={form.groupType} onChange={handleChange}
                className={`w-full border rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb ${fieldErrors.groupType ? 'border-dr' : 'border-[#E5E7EB]'}`}
              >
                <option value="">Select group type</option>
                {(isPadelante ? PADELANTE_GROUP_TYPES : groupTypesList).map((g) => <option key={g}>{g}</option>)}
              </select>
              <FieldError msg={fieldErrors.groupType} />
            </div>

            {/* LLS group — decides which centres participants pick from */}
            {isLlsGroupType(form.groupType) && (
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">LLS Group</label>
                <select
                  name="llsGroup" value={form.llsGroup} onChange={handleChange}
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                >
                  <option value="">Select group</option>
                  {LLS_GROUPS.map((g) => <option key={g} value={g}>Group {g}</option>)}
                </select>
                <p className="text-[11px] text-[#9CA3AF] mt-1">Participants choose their centre from this group. You can set or change it later.</p>
              </div>
            )}

            {/* Number of squads — football only */}
            {form.sport === 'Football' && (
              <div>
                <label className="block text-[13px] font-medium text-[#374151] mb-1">Number of Squads</label>
                <input
                  type="number" name="numberOfSquads" value={form.numberOfSquads} onChange={handleChange}
                  min="1" placeholder="e.g. 2"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
                <p className="text-[12px] text-[#9CA3AF] mt-1">Each squad requires its own fixtures and training sessions.</p>
              </div>
            )}

            {/* Players male/female grouped */}
            <div className="sm:col-span-2">
              <label className="block text-[13px] font-medium text-[#374151] mb-2">Approx Players/Students <span className="text-dr">*</span></label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] text-[#6B7280] mb-1">Male</label>
                  <div className="flex items-center">
                    <button type="button" onClick={() => adjustCount('participantsMale', -1)}
                      className="w-9 h-9 rounded-l-[8px] border border-r-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">−</button>
                    <input type="number" name="participantsMale" value={form.participantsMale} onChange={handleChange}
                      min="0" placeholder="0"
                      className={`w-full h-9 border text-center text-[14px] focus:outline-none focus:ring-2 focus:ring-pb ${fieldErrors.participantsMale ? 'border-dr' : 'border-[#E5E7EB]'}`} />
                    <button type="button" onClick={() => adjustCount('participantsMale', 1)}
                      className="w-9 h-9 rounded-r-[8px] border border-l-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">+</button>
                  </div>
                </div>
                <div>
                  <label className="block text-[12px] text-[#6B7280] mb-1">Female</label>
                  <div className="flex items-center">
                    <button type="button" onClick={() => adjustCount('participantsFemale', -1)}
                      className="w-9 h-9 rounded-l-[8px] border border-r-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">−</button>
                    <input type="number" name="participantsFemale" value={form.participantsFemale} onChange={handleChange}
                      min="0" placeholder="0"
                      className="w-full h-9 border border-[#E5E7EB] text-center text-[14px] focus:outline-none focus:ring-2 focus:ring-pb" />
                    <button type="button" onClick={() => adjustCount('participantsFemale', 1)}
                      className="w-9 h-9 rounded-r-[8px] border border-l-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">+</button>
                  </div>
                </div>
              </div>
              <FieldError msg={fieldErrors.participantsMale} />
            </div>

            {/* Supporters */}
            <div className="sm:col-span-2">
              <label className="block text-[13px] font-medium text-[#374151] mb-2">Approx supporters/other travellers</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] text-[#6B7280] mb-1">Male</label>
                  <div className="flex items-center">
                    <button type="button" onClick={() => adjustCount('supportersMale', -1)}
                      className="w-9 h-9 rounded-l-[8px] border border-r-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">−</button>
                    <input type="number" name="supportersMale" value={form.supportersMale} onChange={handleChange}
                      min="0" placeholder="0"
                      className="w-full h-9 border border-[#E5E7EB] text-center text-[14px] focus:outline-none focus:ring-2 focus:ring-pb" />
                    <button type="button" onClick={() => adjustCount('supportersMale', 1)}
                      className="w-9 h-9 rounded-r-[8px] border border-l-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">+</button>
                  </div>
                </div>
                <div>
                  <label className="block text-[12px] text-[#6B7280] mb-1">Female</label>
                  <div className="flex items-center">
                    <button type="button" onClick={() => adjustCount('supportersFemale', -1)}
                      className="w-9 h-9 rounded-l-[8px] border border-r-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">−</button>
                    <input type="number" name="supportersFemale" value={form.supportersFemale} onChange={handleChange}
                      min="0" placeholder="0"
                      className="w-full h-9 border border-[#E5E7EB] text-center text-[14px] focus:outline-none focus:ring-2 focus:ring-pb" />
                    <button type="button" onClick={() => adjustCount('supportersFemale', 1)}
                      className="w-9 h-9 rounded-r-[8px] border border-l-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">+</button>
                  </div>
                </div>
              </div>
            </div>

            {/* Coaches/Teachers */}
            <div className="sm:col-span-2">
              <label className="block text-[13px] font-medium text-[#374151] mb-2">Coaches/Teachers attending</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] text-[#6B7280] mb-1">Male</label>
                  <div className="flex items-center">
                    <button type="button" onClick={() => adjustCount('customerStaffMale', -1)}
                      className="w-9 h-9 rounded-l-[8px] border border-r-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">−</button>
                    <input type="number" name="customerStaffMale" value={form.customerStaffMale} onChange={handleChange}
                      min="0" placeholder="0"
                      className="w-full h-9 border border-[#E5E7EB] text-center text-[14px] focus:outline-none focus:ring-2 focus:ring-pb" />
                    <button type="button" onClick={() => adjustCount('customerStaffMale', 1)}
                      className="w-9 h-9 rounded-r-[8px] border border-l-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">+</button>
                  </div>
                </div>
                <div>
                  <label className="block text-[12px] text-[#6B7280] mb-1">Female</label>
                  <div className="flex items-center">
                    <button type="button" onClick={() => adjustCount('customerStaffFemale', -1)}
                      className="w-9 h-9 rounded-l-[8px] border border-r-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">−</button>
                    <input type="number" name="customerStaffFemale" value={form.customerStaffFemale} onChange={handleChange}
                      min="0" placeholder="0"
                      className="w-full h-9 border border-[#E5E7EB] text-center text-[14px] focus:outline-none focus:ring-2 focus:ring-pb" />
                    <button type="button" onClick={() => adjustCount('customerStaffFemale', 1)}
                      className="w-9 h-9 rounded-r-[8px] border border-l-0 border-[#E5E7EB] bg-surface text-[#6B7280] hover:bg-[#F3F4F6] text-lg font-medium transition flex items-center justify-center">+</button>
                  </div>
                </div>
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-[13px] font-medium text-[#374151] mb-1">Additional Requirements</label>
              <textarea
                name="notes" value={form.notes} onChange={handleChange}
                rows={4}
                placeholder="Any specific requests, accommodation preferences, match arrangements, budget range, etc."
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[14px] focus:outline-none focus:ring-2 focus:ring-pb resize-none"
              />
            </div>
          </div>
        </section>
      )}

      {/* ─── Step 4: Review ───────────────────────────────────────────────────── */}
      {step === 4 && (
        <div className="space-y-4">
          {/* Brand */}
          <section className="bg-white rounded-[12px] border border-[#E5E7EB] p-5">
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-2">Brand</h3>
            <div className="flex items-center gap-2">
              {BRAND_BADGES[brand] && (
                <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-[4px] ${BRAND_BADGES[brand].className}`}>{BRAND_BADGES[brand].label}</span>
              )}
              <span className="text-[13px] text-[#111827] font-medium">
                {BRANDS.find((b) => b.id === brand)?.label}
              </span>
            </div>
          </section>
          {/* Customer */}
          <section className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 space-y-3">
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">{t("newTour.customer")}</h3>
            <ReviewRow label="Name" value={selectedCustomer?.name} />
            <ReviewRow label="Email" value={selectedCustomer?.email} />
            <ReviewRow label="Group Name" value={form.groupName || null} />
            <ReviewRow label="Contact Name" value={form.contactName} />
            <ReviewRow label="Phone" value={form.phone || null} />
          </section>

          {/* Tour details */}
          <section className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 space-y-3">
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">{t("newTour.tourDetails")}</h3>
            <ReviewRow label="Sport" value={form.sport} />
            <ReviewRow label="Destination" value={form.destination} />
            <ReviewRow label="Departure" value={formatDate(form.departureDate)} />
            <ReviewRow label="Return" value={formatDate(form.returnDate)} />
            {Object.keys(selectedAddons).length > 0 && (
              <div className="flex gap-3">
                <span className="text-[13px] text-[#6B7280] w-40 flex-shrink-0">What's included</span>
                <div className="flex flex-wrap gap-1.5">
                  {Object.values(selectedAddons).map((v) => (
                    <span key={v.name} className="px-[9px] py-[3px] rounded-[12px] bg-blue-light border border-pb/20 text-[11px] font-semibold text-pb">{v.name}</span>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Group info */}
          <section className="bg-white rounded-[12px] border border-[#E5E7EB] p-6 space-y-3">
            <h3 className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px]">{t("newTour.groupInfo")}</h3>
            <ReviewRow label="Group Type" value={form.groupType} />
            {isLlsGroupType(form.groupType) && <ReviewRow label="LLS Group" value={form.llsGroup ? `Group ${form.llsGroup}` : null} />}
            <ReviewRow label="Players/Students" value={form.participants} />
            {form.numberOfSquads && <ReviewRow label="Number of Squads" value={form.numberOfSquads} />}
            <ReviewRow label="Male players" value={form.participantsMale || null} />
            <ReviewRow label="Female players" value={form.participantsFemale || null} />
            <ReviewRow label="Male supporters" value={form.supportersMale || null} />
            <ReviewRow label="Female supporters" value={form.supportersFemale || null} />
            <ReviewRow label="Male coaches/teachers" value={form.customerStaffMale || null} />
            <ReviewRow label="Female coaches/teachers" value={form.customerStaffFemale || null} />
            {form.notes && (
              <div className="flex gap-3">
                <span className="text-[13px] text-[#6B7280] w-40 flex-shrink-0">Additional requirements</span>
                <span className="text-[13px] text-[#111827] font-medium whitespace-pre-wrap">{form.notes}</span>
              </div>
            )}
          </section>

          {fieldErrors.submit && (
            <p className="text-[13px] text-dr bg-red-light border border-dr/20 rounded-[8px] px-4 py-3">{fieldErrors.submit}</p>
          )}
        </div>
      )}

      {/* ─── Navigation ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between pb-6">
        {step > 1 ? (
          <button
            type="button" onClick={goBack}
            className="px-[18px] py-[9px] text-[13px] font-medium text-[#374151] border border-[#E5E7EB] rounded-[8px] hover:bg-surface transition"
          >
            {t('newTour.back')}
          </button>
        ) : (
          <div />
        )}

        <span className="text-[13px] text-[#9CA3AF]">{t('newTour.stepOf', { n: step })}</span>

        {step < 4 ? (
          <button
            type="button" onClick={goNext}
            className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition"
          >
            {t('newTour.continue')}
          </button>
        ) : (
          <button
            type="button" onClick={handleSubmit} disabled={submitting}
            className="px-[18px] py-[9px] text-[13px] font-semibold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60"
          >
            {submitting ? t('newTour.creating') : t('newTour.createRequest')}
          </button>
        )}
      </div>

    </div>
  );
}
